// deps
import { useCallback, useContext } from "react";

// internal-deps
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// analytics
import {
    clickDoneDineoutBookingSuccess,
    clickViewHelpDineoutBookingSuccess,
    impressionDineoutBookingSuccess,
    impressionBillDetailsDineoutBookingSuccess,
    impressionDownloadCta,
    clickDownloadCta,
    clickDoneEventCTA,
    impressionQrCode,
    clickShareIcon,
    impressionBillDetailsEvent,
    screenViewBookingSuccess,
    impressionEventNudge,
    clickEventNudge,
    clickCancelBookingPopupCta,
    clickCallRestaurantCTA,
    clickTnCDineoutBookingSuccess,
    clickViewRestaurantDetails,
    clickMapsCTA,
    clickCancelBookingCta,
    clickBookingTicketCta,
    clickBackButtonBookingDetails,
    impressionBenefitCallout,
    clickThingsToRemember,
    impressionBookingCard,
    impressionYourBookings,
    impressionCashBackBenefitCallout,
    impressionDinecashPoolBanner,
} from "@dineout-payment-success-ui/analytics/events";

// interfaces
import { IBillEntries, IBookingDetails } from "@dineout-payment-success-ui/interfaces";
import { getBillEntriesList } from "@dineout-payment-success-ui/helpers/PaymentSuccessHelper";
import {
    IClickThingsToRemember,
    IEventContext,
    IImpressionBookingCard,
    IImpressionYourBookings,
    IClickCancelBookingCta,
    IClickCancelBookingPopupCta,
    IClickTnCDineoutBookingSuccess,
    IImpressionCashBackBenefitCallout,
} from "@dineout-payment-success-ui/interfaces/AnalyticsEvents";

export const useDineoutBookingSuccess = () => {
    const { clickEvent, impressionEvent, screenViewEvent } = useContext(AnalyticsContext);

    const onClickViewHelpDineoutBookingSuccess = useCallback(
        (orderId: string, isCheckoutFlow: boolean) => {
            clickViewHelpDineoutBookingSuccess(clickEvent, isCheckoutFlow, orderId);
        },
        [clickEvent],
    );

    const onImpressionDinecashPoolBanner = useCallback(
        ({ orderId, restaurantId }: { orderId: string; restaurantId: string }) => {
            impressionDinecashPoolBanner(impressionEvent, { orderId, restaurantId });
        },
        [impressionEvent],
    );

    const onImpressionDineoutBookingSuccess = useCallback(
        (isCheckoutFlow: boolean, data: { orderId: string; bookingStatus: string }) => {
            impressionDineoutBookingSuccess(impressionEvent, isCheckoutFlow, data);
        },
        [impressionEvent],
    );

    const onImpressionCashBackBenefitCallout = useCallback(
        (prop: IImpressionCashBackBenefitCallout) => {
            const { bannerText, dealCategory, restaurantId } = prop;
            impressionCashBackBenefitCallout(impressionEvent, {
                bannerText,
                dealCategory,
                restaurantId,
            });
        },
        [impressionEvent],
    );

    const onClickDoneDineoutBookingSuccess = useCallback(
        (orderId: string, isCheckoutFlow: boolean, dealCategory: string) => {
            clickDoneDineoutBookingSuccess(clickEvent, isCheckoutFlow, orderId, dealCategory);
        },
        [clickEvent],
    );

    const onBillDetailsImpressionDineoutBookingSuccess = useCallback(
        ({
            ids,
            bookingDetails,
            billDetailsInfo,
            eventData,
            isCheckoutFlow,
        }: {
            ids: { restaurantId: number | string; eventId: string };
            bookingDetails: IBookingDetails;
            billDetailsInfo: IBillEntries[];
            eventData: {
                ticketDetails: Record<string, number>;
                guestCount?: number;
                dealCategory?: string;
                isEventReservation?: boolean;
            };
            isCheckoutFlow: boolean;
        }) => {
            const billDetails = getBillEntriesList(billDetailsInfo);
            const { restaurantId, eventId } = ids;
            const { ticketDetails, guestCount, dealCategory, isEventReservation } = eventData;

            if (isEventReservation) {
                impressionBillDetailsEvent(impressionEvent, {
                    dealCategory: dealCategory || "",
                    restaurantID: restaurantId,
                    eventId,
                    guests: guestCount || 0,
                    billDetails,
                    ticketDetails,
                });
            } else {
                impressionBillDetailsDineoutBookingSuccess(impressionEvent, isCheckoutFlow, {
                    dealCategory: dealCategory || "",
                    restaurantID: restaurantId,
                    guests: bookingDetails?.noOfGuests,
                    billDetails,
                });
            }
        },
        [impressionEvent],
    );

    const onImpressionDownloadCTABookingSuccess = () => {
        impressionDownloadCta(impressionEvent);
    };

    const onClickDownloadCTABookingSuccess = () => {
        clickDownloadCta(clickEvent);
    };

    const onClickDoneEventCTA = (
        isCheckoutFlow: boolean,
        { orderId, restaurantId, eventId, dealCategory, guests }: IEventContext,
    ) => {
        clickDoneEventCTA(clickEvent, isCheckoutFlow, { orderId, restaurantId, eventId, dealCategory, guests });
    };

    const onImpressionQrCode = ({ orderId, restaurantId, eventId, dealCategory }: IEventContext) => {
        impressionQrCode(impressionEvent, { orderId, restaurantId, eventId, dealCategory });
    };

    const onImpressionEventNudge = ({ orderId, restaurantId, eventId, dealCategory }: IEventContext) => {
        impressionEventNudge(impressionEvent, { orderId, restaurantId, eventId, dealCategory });
    };

    const onClickShareIcon = (
        isCheckoutFlow: boolean,
        { orderId, restaurantId, eventId, dealCategory }: IEventContext,
    ) => {
        clickShareIcon(clickEvent, isCheckoutFlow, { orderId, restaurantId, eventId, dealCategory });
    };

    const onClickEventNudge = ({ orderId, restaurantId, eventId, dealCategory }: IEventContext) => {
        clickEventNudge(clickEvent, { orderId, restaurantId, eventId, dealCategory });
    };

    const onClickCancelBookingCta = (isCheckoutFlow: boolean, props: IClickCancelBookingCta) => {
        clickCancelBookingCta(clickEvent, isCheckoutFlow, props);
    };

    const onClickCancelBookingPopupCta = (isCheckoutFlow: boolean, props: IClickCancelBookingPopupCta) => {
        clickCancelBookingPopupCta(clickEvent, isCheckoutFlow, props);
    };

    const onClickCallRestaurantCTA = (
        isCheckoutFlow: boolean,
        { orderId, dealCategory }: { orderId: string; dealCategory: string },
    ) => {
        clickCallRestaurantCTA(clickEvent, isCheckoutFlow, { orderId, dealCategory });
    };

    const onClickTnCDineoutBookingSuccess = (isCheckoutFlow: boolean, props: IClickTnCDineoutBookingSuccess) => {
        clickTnCDineoutBookingSuccess(clickEvent, isCheckoutFlow, props);
    };

    const onClickThingsToRemember = (
        isCheckoutFlow: boolean,
        { orderId, restaurantId, guests, dealCategory }: IClickThingsToRemember,
    ) => {
        clickThingsToRemember(clickEvent, isCheckoutFlow, { orderId, restaurantId, guests, dealCategory });
    };

    const onClickViewRestaurantDetails = (isCheckoutFlow: boolean, { orderId }: { orderId: string }) => {
        clickViewRestaurantDetails(clickEvent, isCheckoutFlow, { orderId });
    };

    const onClickMapsCTA = (
        isCheckoutFlow: boolean,
        { orderId, dealCategory }: { orderId: string; dealCategory: string },
    ) => {
        clickMapsCTA(clickEvent, isCheckoutFlow, { orderId, dealCategory });
    };

    const onScreenViewBookingSuccess = ({
        billAmount,
        restId,
        eventId,
        dealCategory,
    }: {
        restId: string;
        eventId: string;
        dealCategory: string;
        billAmount: number;
    }) => {
        screenViewBookingSuccess(screenViewEvent, {
            billAmount,
            restId,
            eventId,
            dealCategory,
        });
    };

    const onClickBookingTicketCta = (
        isCheckoutFlow: boolean,
        {
            ctaTitle,
            ctaDeeplink,
            orderId,
            dealCategory,
        }: { ctaTitle: string; ctaDeeplink: string; orderId: string; dealCategory: string },
    ) => {
        clickBookingTicketCta(clickEvent, isCheckoutFlow, { ctaTitle, ctaDeeplink, orderId, dealCategory });
    };

    const onClickBackButtonBookingDetails = () => {
        clickBackButtonBookingDetails(clickEvent);
    };

    const onImpressionBenefitCallout = (isCheckoutFlow: boolean, { offerText }: { offerText: string }) => {
        impressionBenefitCallout(impressionEvent, isCheckoutFlow, { offerText });
    };

    const onImpressionBookingCard = (isCheckoutFlow: boolean, props: IImpressionBookingCard) => {
        impressionBookingCard(impressionEvent, isCheckoutFlow, props);
    };

    const onImpressionYourBookings = (isCheckoutFlow: boolean, props: IImpressionYourBookings) => {
        impressionYourBookings(impressionEvent, isCheckoutFlow, props);
    };

    return {
        onClickViewHelpDineoutBookingSuccess,
        onImpressionDineoutBookingSuccess,
        onClickDoneDineoutBookingSuccess,
        onBillDetailsImpressionDineoutBookingSuccess,
        onImpressionDownloadCTABookingSuccess,
        onClickDownloadCTABookingSuccess,
        onClickDoneEventCTA,
        onImpressionQrCode,
        onClickShareIcon,
        onScreenViewBookingSuccess,
        onImpressionEventNudge,
        onClickEventNudge,
        onClickMapsCTA,
        onClickCancelBookingPopupCta,
        onClickCallRestaurantCTA,
        onClickTnCDineoutBookingSuccess,
        onClickViewRestaurantDetails,
        onClickCancelBookingCta,
        onClickBookingTicketCta,
        onClickBackButtonBookingDetails,
        onImpressionBenefitCallout,
        onClickThingsToRemember,
        onImpressionBookingCard,
        onImpressionYourBookings,
        onImpressionCashBackBenefitCallout,
        onImpressionDinecashPoolBanner,
    };
};
